import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["background", "wrapper"];
  static values = { skipOpen: Boolean };

  connect() {
    // Get all existing drawer elements
    const existingBackgrounds = document.querySelectorAll(
      '[data-ui--drawer-target="background"]',
    );
    const existingWrappers = document.querySelectorAll(
      '[data-ui--drawer-target="wrapper"]',
    );

    // Find the highest z-index among existing elements
    const getHighestZIndex = (elements) => {
      return Math.max(
        ...Array.from(elements).map(
          (el) => parseInt(window.getComputedStyle(el).zIndex) || 0,
        ),
      );
    };

    const highestZIndex = Math.max(
      getHighestZIndex(existingBackgrounds),
      getHighestZIndex(existingWrappers),
    );

    // Set new z-index (highest + 1)
    const newZIndex = highestZIndex + 1;
    this.backgroundTarget.style.zIndex = newZIndex;
    this.wrapperTarget.style.zIndex = newZIndex;

    // Check if drawer should skip animation (already open)
    const skipAnimation = this.element.dataset.skipOpenValue === "true";

    if (skipAnimation) {
      // Drawer is already open, remove transitions and open immediately
      this.backgroundTarget.classList.remove(
        "transition-opacity",
        "duration-300",
      );
      this.wrapperTarget.classList.remove(
        "transition-transform",
        "duration-300",
      );
      this.backgroundTarget.classList.add("opacity-100");
      this.wrapperTarget.classList.remove("translate-x-full");
      document.querySelector("body").classList.add("overflow-hidden");
    } else {
      requestAnimationFrame(() => {
        this.backgroundTarget.classList.add("opacity-100");
        this.wrapperTarget.classList.remove("translate-x-full");
        document.querySelector("body").classList.add("overflow-hidden");
      });
    }
    document.addEventListener("keydown", this.handleKeydown);

    // Dispatch event to close any open dropdowns
    window.dispatchEvent(new CustomEvent("drawer:opened"));
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    // Ensure body scroll is restored when drawer is removed from DOM
    document.querySelector("body").classList.remove("overflow-hidden");
  }

  handleKeydown = (event) => {
    if (event.key === "Escape") {
      this.close();
    }
  };

  open() {
    // Dispatch event to close any open dropdowns
    window.dispatchEvent(new CustomEvent("drawer:opened"));

    if (this.skipOpenValue) {
      // Already open, no animation
      this.backgroundTarget.classList.add("opacity-100");
      this.wrapperTarget.classList.remove("translate-x-full");
      document.querySelector("body").classList.add("overflow-hidden");
      this.focusAutofocusElement();
    } else {
      // Animate open
      requestAnimationFrame(() => {
        this.backgroundTarget.classList.add("opacity-100");
        this.wrapperTarget.classList.remove("translate-x-full");
        document.querySelector("body").classList.add("overflow-hidden");

        // Focus after animation starts
        setTimeout(() => this.focusAutofocusElement(), 100);
      });
    }
  }

  focusAutofocusElement() {
    const autofocusElement = this.element.querySelector("[autofocus]");
    if (autofocusElement) {
      autofocusElement.focus();
    }
  }

  close() {
    this.backgroundTarget.classList.remove("opacity-100");
    this.wrapperTarget.classList.add("translate-x-full");
    document.querySelector("body").classList.remove("overflow-hidden");

    setTimeout(() => {
      const frame = this.element.closest("turbo-frame");
      if (frame) {
        frame.src = undefined;
        frame.innerHTML = "";
      } else {
        this.element.remove();
      }
    }, 300);
  }
}
